import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import { Activity, Company, EntityType } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import User from "@mapmycustomers/shared/types/User";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { LoadingSpinner } from "@mapmycustomers/ui";

import CreateActivityModal from "@app/component/createEditEntity/Activity/CreateActivityModal";
import useDataRefreshing from "@app/component/preview/util/useDataRefreshing";
import RecapRange from "@app/enum/preview/RecapRange";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useModalVisibilityAnalyticEffect from "@app/util/hook/useModalVisibilityAnalyticEffect";

import { companyAnalytics } from "../../analytics";
import ActivitiesPanel from "../../components/ActivityPanel";
import AddOrCreateButton from "../../components/AddOrCreateButton";
import EditActivities from "../../components/EditActivities";
import Section from "../../components/Section";
import TabLayout from "../../components/TabLayout";
import {
  changeActivitiesAssignees,
  changeActivitiesFilter,
  changeActivitiesOrder,
  changeActivitiesRecapRange,
  changeActivitiesSearchQuery,
  changeActivitiesSelectedActivityTypes,
  fetchCompanyActivities,
  fetchCompanyActivitiesCompletedByType,
  fetchCompanyActivitiesTotal,
  postponeActivity,
  toggleCompleteActivity,
  updateActivityNote,
} from "../store/actions";
import {
  getActivitiesFilter,
  getActivitiesOrder,
  getActivitiesSearchQuery,
  getActivitiesSelectedAssignees,
  getPostponeActivityLoadingIds,
  getRecapRange,
  getSelectedActivityTypes,
  getToggleCompleteActivityLoadingIds,
  isActivitiesCompletedByTypeLoading,
  isActivitiesLoading,
  isActivitiesTotalLoading,
} from "../store/selectors";

import styles from "./Activities.module.scss";

interface Props {
  activities: Activity[];
  activitiesCompletedByType: Activity[];
  activitiesCompletedByTypeLoading: boolean;
  activitiesTotal: number;
  activitiesTotalLoading: boolean;
  changeActivitiesAssignees: typeof changeActivitiesAssignees;
  changeActivitiesFilter: typeof changeActivitiesFilter;
  changeActivitiesOrder: typeof changeActivitiesOrder;
  changeActivitiesRecapRange: typeof changeActivitiesRecapRange;
  changeActivitiesSearchQuery: typeof changeActivitiesSearchQuery;
  changeActivitiesSelectedActivityTypes: typeof changeActivitiesSelectedActivityTypes;
  company: Company;
  fetchCompanyActivities: typeof fetchCompanyActivities.request;
  fetchCompanyActivitiesCompletedByType: typeof fetchCompanyActivitiesCompletedByType.request;
  fetchCompanyActivitiesTotal: typeof fetchCompanyActivitiesTotal.request;
  filter: ActivityStatusOption;
  isReadOnlyMember: boolean;
  loading: boolean;
  order: SortModel;
  postponeActivity: typeof postponeActivity.request;
  postponeActivityLoadingIds: Activity["id"][];
  recapRange: RecapRange;
  search: string;
  selectedActivityTypes: ActivityType[];
  selectedAssignees: User["id"][];
  subscribe: typeof subscribe;
  toggleCompleteActivity: typeof toggleCompleteActivity.request;
  toggleCompleteActivityLoadingIds: Activity["id"][];
  unsubscribe: typeof unsubscribe;
  updateActivityNote: typeof updateActivityNote.request;
}

const Activities: React.FC<Props> = ({
  activities,
  activitiesCompletedByType,
  activitiesCompletedByTypeLoading,
  activitiesTotal,
  activitiesTotalLoading,
  changeActivitiesAssignees,
  changeActivitiesFilter,
  changeActivitiesOrder,
  changeActivitiesRecapRange,
  changeActivitiesSearchQuery,
  changeActivitiesSelectedActivityTypes,
  company,
  fetchCompanyActivities,
  fetchCompanyActivitiesCompletedByType,
  fetchCompanyActivitiesTotal,
  filter,
  isReadOnlyMember,
  loading,
  order,
  postponeActivity,
  postponeActivityLoadingIds,
  recapRange,
  search,
  selectedActivityTypes,
  selectedAssignees,
  subscribe,
  toggleCompleteActivity,
  toggleCompleteActivityLoadingIds,
  unsubscribe,
  updateActivityNote,
}) => {
  const intl = useIntl();
  const [createActivityModalVisible, showCreateActivityModal, hideCreateActivityModal] =
    useBoolean();

  const fetchAll = useCallback(() => {
    fetchCompanyActivities({
      activityTypes: selectedActivityTypes,
      company,
      filter,
      order,
      recapRange,
      search,
      selectedAssignees,
    });
    fetchCompanyActivitiesCompletedByType({
      activityTypes: selectedActivityTypes,
      company,
      filter,
      order,
      recapRange,
      selectedAssignees,
    });
    fetchCompanyActivitiesTotal(company);
  }, [
    company,
    fetchCompanyActivities,
    fetchCompanyActivitiesCompletedByType,
    fetchCompanyActivitiesTotal,
    filter,
    order,
    recapRange,
    search,
    selectedActivityTypes,
    selectedAssignees,
  ]);

  useDataRefreshing([EntityType.ACTIVITY], fetchAll, subscribe, unsubscribe);

  const doFetchCompanyActivities = useCallback(
    (search: string, filter: ActivityStatusOption) => {
      fetchCompanyActivities({
        activityTypes: selectedActivityTypes,
        company,
        filter,
        order,
        recapRange,
        search,
        selectedAssignees,
      });
    },
    [company, fetchCompanyActivities, order, recapRange, selectedActivityTypes, selectedAssignees]
  );

  const analyticIssuer = useExtendedAnalytics("Activities", companyAnalytics);
  useModalVisibilityAnalyticEffect("Create Activity", createActivityModalVisible, analyticIssuer);

  useEffect(() => {
    fetchCompanyActivities({
      activityTypes: selectedActivityTypes,
      company,
      filter,
      order,
      recapRange,
      search,
      selectedAssignees,
    });
    fetchCompanyActivitiesCompletedByType({
      activityTypes: selectedActivityTypes,
      company,
      filter,
      order,
      recapRange,
      selectedAssignees,
    });
  }, [
    company,
    fetchCompanyActivities,
    fetchCompanyActivitiesCompletedByType,
    filter,
    order,
    recapRange,
    search,
    selectedActivityTypes,
    selectedAssignees,
  ]);

  const handleHideCreateActivity = useCallback(
    (updated?: boolean) => {
      hideCreateActivityModal();
      if (updated) {
        fetchAll();
      }
    },
    [fetchAll, hideCreateActivityModal]
  );

  return (
    <TabLayout
      action={
        <AddOrCreateButton
          disabled={isReadOnlyMember}
          label={intl.formatMessage({
            id: "companyRecordPane.activities.create",
            defaultMessage: "Add",
            description: "Title of add button for activities",
          })}
          onClick={showCreateActivityModal}
          tooltip={intl.formatMessage({
            id: "companyRecordPane.activities.create.tooltip",
            defaultMessage: "Create Activity",
            description: "Title of a Create Activity button on a record pane",
          })}
        />
      }
      title={
        activitiesTotalLoading ? (
          <LoadingSpinner mini />
        ) : (
          intl.formatMessage(
            {
              id: "companyRecordPane.activities.title",
              defaultMessage: "Activities <em>({currentCount} / {totalCount})</em>",
              description: "Title of an Activities section on a record pane",
            },
            {
              currentCount: activities.length,
              em: (text: string[]) => <span className={styles.total}>{text}</span>,
              totalCount: activitiesTotal,
            }
          )
        )
      }
    >
      <Section>
        <ActivitiesPanel
          activities={activitiesCompletedByType}
          activitiesTotal={activitiesTotal}
          analyticIssuer={analyticIssuer}
          filter={filter}
          loading={activitiesCompletedByTypeLoading}
          onChangeActivityTypes={changeActivitiesSelectedActivityTypes}
          onChangeAssignees={changeActivitiesAssignees}
          onChangeRecapRange={changeActivitiesRecapRange}
          onFilterChange={changeActivitiesFilter}
          recapRange={recapRange}
          selectedActivityTypes={selectedActivityTypes}
          selectedAssignees={selectedAssignees}
        />
      </Section>
      <Section className={styles.allActivitiesSection}>
        <EditActivities
          activities={activities}
          activitiesFetcher={doFetchCompanyActivities}
          analyticIssuer={analyticIssuer}
          filter={filter}
          loading={loading}
          onChange={fetchAll}
          onChangeSortState={changeActivitiesOrder}
          onSearchChange={changeActivitiesSearchQuery}
          postponeActivity={postponeActivity}
          postponeActivityLoadingIds={postponeActivityLoadingIds}
          search={search}
          sortState={order}
          toggleCompleteActivity={toggleCompleteActivity}
          toggleCompleteActivityLoadingIds={toggleCompleteActivityLoadingIds}
          updateActivityNote={updateActivityNote}
        />
      </Section>
      {createActivityModalVisible && (
        <CreateActivityModal
          analyticIssuer={analyticIssuer}
          fixedCompany={company}
          onHide={handleHideCreateActivity}
          onSuccess={fetchAll}
        />
      )}
    </TabLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  activitiesCompletedByTypeLoading: isActivitiesCompletedByTypeLoading(state),
  activitiesTotalLoading: isActivitiesTotalLoading(state),
  filter: getActivitiesFilter(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  loading: isActivitiesLoading(state),
  order: getActivitiesOrder(state),
  postponeActivityLoadingIds: getPostponeActivityLoadingIds(state),
  recapRange: getRecapRange(state),
  search: getActivitiesSearchQuery(state),
  selectedActivityTypes: getSelectedActivityTypes(state),
  selectedAssignees: getActivitiesSelectedAssignees(state),
  toggleCompleteActivityLoadingIds: getToggleCompleteActivityLoadingIds(state),
});

const mapDispatchToProps = {
  changeActivitiesAssignees,
  changeActivitiesFilter,
  changeActivitiesOrder,
  changeActivitiesRecapRange,
  changeActivitiesSearchQuery,
  changeActivitiesSelectedActivityTypes,
  fetchCompanyActivities: fetchCompanyActivities.request,
  fetchCompanyActivitiesCompletedByType: fetchCompanyActivitiesCompletedByType.request,
  fetchCompanyActivitiesTotal: fetchCompanyActivitiesTotal.request,
  postponeActivity: postponeActivity.request,
  subscribe,
  toggleCompleteActivity: toggleCompleteActivity.request,
  unsubscribe,
  updateActivityNote: updateActivityNote.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Activities);
